import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore.js';
import { useEditorStore } from '../stores/editorStore.js';
import { useAiComposer } from '../stores/aiComposer.js';

// ─────────────────────────────────────────────────────────────────
// Workspace cache purge
//
// Every workspace-scoped route (posts, emails, components, email
// senders, members/invites, billing) resolves its workspace from the
// `X-Workspace-Id` header — but several of those queries are keyed
// WITHOUT the workspace id, e.g. `['posts', { search, status }]` in
// DashboardPage. Switching the active workspace therefore leaves the
// previous workspace's rows cached under keys the new workspace reuses,
// and because the key itself never changes the page does not even
// refetch: it silently keeps rendering another workspace's content.
//
// `resetQueries` is what fixes that. It is NOT the same as `clear`:
// clearing removes query objects while mounted observers keep reporting
// their last result, so the UI stays on the old workspace's rows (this
// was measured against @tanstack/query-core 5.91 — `clear` left a
// mounted observer reporting the previous workspace's data with
// `fetchStatus: idle`, i.e. no refetch ever happened). `resetQueries`
// drops the data of every query, mounted or not, and refetches the
// mounted ones, which is exactly "purge and reload".
//
// There is no service worker in this app and the API sets no
// client-side cache headers, so the React Query cache plus the two
// in-memory editor stores below are the whole browser-side surface for
// workspace data.
// ─────────────────────────────────────────────────────────────────

/** Drop the in-memory document state that belongs to the workspace being left. */
function resetWorkspaceScopedStores(): void {
  // An open document would otherwise stay on screen, its undo stack could
  // restore the old workspace's blocks, and — because `reset` clears
  // `isDirty` — an armed 2s autosave debounce is dropped rather than fired
  // against a document that does not exist in the new workspace.
  useEditorStore.getState().reset();
  useEditorStore.temporal.getState().clear();
  useAiComposer.getState().closeComposer();
}

/**
 * Change the active workspace and purge the previous one's browser cache.
 *
 * The order is load-bearing: `resetQueries` refetches the mounted queries,
 * so it must run AFTER the active workspace changes — resetting first would
 * send every one of those refetches straight back to the workspace being
 * left. The leading `cancelQueries` is defensive: it stops requests already
 * in flight (which carry the old `X-Workspace-Id`, because `hooks/api.ts`
 * reads the header when the request is issued) before state flips.
 * `resetQueries` cancels in-flight fetches on its own too, so this is
 * belt-and-braces rather than the thing doing the work.
 */
export async function switchWorkspace(
  queryClient: QueryClient,
  workspaceId: string,
): Promise<void> {
  await queryClient.cancelQueries();
  useAuthStore.getState().setActiveWorkspace(workspaceId);
  // Deliberately not awaited: the switch should not block on refetches, and
  // mounted observers re-render as each new result arrives.
  void queryClient.resetQueries();
  resetWorkspaceScopedStores();
}

/**
 * Wipe all cached workspace data without changing the active workspace.
 *
 * Used on sign-out: the query cache outlives the session, so without this
 * the next person to sign in on the same browser would be shown the
 * previous user's workspaces.
 */
export async function purgeWorkspaceCaches(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries();
  await queryClient.resetQueries();
  resetWorkspaceScopedStores();
}
